import * as React from "react";
import {Grid, Icon, Label, ButtonGroup, Button, Input} from "semantic-ui-react";
import {useState, useEffect, useRef} from "react";
import styled, {keyframes} from "styled-components";
import ddebit from "../../resources/images/payment/directdebit.png";
import _ from 'lodash';
import {DisplayMode} from "../AddressSelector/AddressSelector";

interface IDirectDebitCaptureProps {
    onValid(
        valid: boolean,
        holdsUKBankAccountAndIsOwner: number,
        solePersonRequiredToAuthoriseDirectDebit: number,
        accountName: string,
        sortCode: string,
        sortCodePart1: string,
        sortCodePart2: string,
        sortCodePart3: string,
        accountNumber: string,
        continueButtonClicked: boolean
    ): void;

    focus: boolean;
    loading: boolean;
    canSetupDirectDebit?: boolean;
    showCancelButton?: boolean;
    hideDataEntryFields?: boolean;

    error?: string;
    bankDetails?: string;
    containerTitle?: string;

    bankAccountValidationResponse?: {}

    onContinue(): void;

    onReset(): void;

    onSetupDirectDebit(
        holdsUKBankAccountAndIsOwner: number,
        solePersonRequiredToAuthoriseDirectDebit: number,
        accountName: string,
        sortCode: string,
        sortCodePart1: string,
        sortCodePart2: string,
        sortCodePart3: string,
        accountNumber: string,
        accountInformationIsValid: boolean,
        accountSupportsDirectDebit: boolean,
        bankValidationResponse: {}
    ): void;

    onCancel(): void;
}

export const DirectDebitCapture: React.FC<IDirectDebitCaptureProps> = ({
                                                                           onValid,
                                                                           focus,
                                                                           loading,
                                                                           canSetupDirectDebit,
                                                                           onContinue,
                                                                           onSetupDirectDebit,
                                                                           onCancel,
                                                                           onReset,
                                                                           error,
                                                                           bankDetails,
                                                                           bankAccountValidationResponse,
                                                                           showCancelButton,
                                                                           hideDataEntryFields,
                                                                           containerTitle
                                                                       }) => {

    const alphaInputPattern = /^[a-zA-Z '-]*$/;
    const numericInputPattern = /^[0-9]*$/;

    const [dd, setDirectDebitInfo] = useState({
        accountName: "",
        sortCode: "",
        sortCodePart1: "",
        sortCodePart2: "",
        sortCodePart3: "",
        accountNumber: "",
        bankName: ""
    });

    const [isV, setIsValid] = useState(false);

    const accountNameRef = useRef<HTMLInputElement>(null);
    const sortCodePart1Ref = useRef<HTMLInputElement>(null);
    const sortCodePart2Ref = useRef<HTMLInputElement>(null);
    const sortCodePart3Ref = useRef<HTMLInputElement>(null);
    const accountNumberRef = useRef<HTMLInputElement>(null);

    const [errorMessage, setErrorMessage] = useState<string | null>(error || null);

    const [bankAccountDetails, setBankAccountDetails] = useState<string | null>(bankDetails || null);
    const [bankAccountValidationResponseData, setBankAccountValidationResponse] = useState<any | null>(bankAccountValidationResponse || null);

    const [displayCancelButton, setDisplayCancelButton] = useState<boolean | null>(showCancelButton || null);
    const [containerTitleText, setContainerTitleText] = useState<string | null>(containerTitle || null);
    const [hiddenDataEntryFields, setHiddenDataEntryFields] = useState<boolean | null>(hideDataEntryFields || null);

    const [displayContinueButton, setDisplayContinueButton] = useState<boolean>(false);
    const [continueButtonClicked, setContinueButtonClicked] = useState<boolean>(false);


    const [holdsUKBankAccountAndIsOwner, setHoldsUKBankAccountAndIsOwnerQuestion] = useState<number>(-1);
    const [solePersonRequiredToAuthoriseDirectDebit, setSolePersonRequiredToAuthoriseDirectDebitQuestion] = useState<number>(-1);

    const [validAnswersToQuestions, setValidAnswersToQuestions] = useState<boolean>(false);

    useEffect(() => {
        if (accountNameRef.current && focus) {
            accountNameRef.current.focus();
        }
    }, [accountNameRef]);

    useEffect(() => {

        if (holdsUKBankAccountAndIsOwner === 1 && solePersonRequiredToAuthoriseDirectDebit === 1) {
            setValidAnswersToQuestions(true);
        } else {
            setValidAnswersToQuestions(false);
        }

        const fieldsValid = (holdsUKBankAccountAndIsOwner === 1 &&
            solePersonRequiredToAuthoriseDirectDebit === 1 &&
            dd.accountName !== "" &&
            dd.sortCodePart1.length === 2 &&
            dd.sortCodePart2.length === 2 &&
            dd.sortCodePart3.length === 2 &&
            dd.accountNumber.length >= 8);

        setIsValid(fieldsValid);

        if (holdsUKBankAccountAndIsOwner === 1 &&
            solePersonRequiredToAuthoriseDirectDebit === 1) {
            setDisplayContinueButton(true);
        } else {
            setDisplayContinueButton(false);
        }

        let fullSortCode = "";
        if (fieldsValid) {
            fullSortCode = dd.sortCodePart1 + dd.sortCodePart2 + dd.sortCodePart3;
        }

        onValid(
            fieldsValid,
            holdsUKBankAccountAndIsOwner,
            solePersonRequiredToAuthoriseDirectDebit,
            dd.accountName,
            fullSortCode,
            dd.sortCodePart1,
            dd.sortCodePart2,
            dd.sortCodePart3,
            dd.accountNumber,
            continueButtonClicked
        );

    }, [dd, holdsUKBankAccountAndIsOwner, solePersonRequiredToAuthoriseDirectDebit, continueButtonClicked]);

    useEffect(() => {

        if (bankDetails) {
            setBankAccountDetails(bankDetails);
        } else {
            setBankAccountDetails(null);
        }
    }, [bankDetails])

    useEffect(() => {

        if (bankAccountValidationResponse) {
            setBankAccountValidationResponse(bankAccountValidationResponse);
        } else {
            setBankAccountValidationResponse(null);
        }
    }, [bankAccountValidationResponse])


    useEffect(() => {

        if (error) {
            setErrorMessage(error)
        } else {
            setErrorMessage(null);
        }
    }, [error])

    useEffect(() => {
        if (showCancelButton) {
            setDisplayCancelButton(showCancelButton)
        } else {
            setDisplayCancelButton(null);
        }
    }, [showCancelButton])

    useEffect(() => {
        if (hideDataEntryFields) {
            setHiddenDataEntryFields(hideDataEntryFields)
        } else {
            setHiddenDataEntryFields(null);
        }
    }, [hideDataEntryFields])


    const formatBankDetails = (bankDetailsString) => {
        var bankDetailsFormatted = "";
        if (bankDetailsString.indexOf(" ") > -1) {
            var bankDetailRows = bankDetailsString.split("|");
            for (var i = 0; i < bankDetailRows.length; i++) {
                if (bankDetailRows[i].trim().length > 0)
                    bankDetailsFormatted += bankDetailRows[i].trim() + "\n";
            }
        } else {
            bankDetailsFormatted = bankDetailsString;
        }
        return bankDetailsFormatted;
    }

    const continueAction = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setContinueButtonClicked(true);
        onContinue();
    };

    const setupDirectDebitAction = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {

        let fullSortCode = "";
        let accountInformationIsValid = false;
        let accountSupportsDirectDebit = false;
        let bankAccountValidationResponseDataJson = {}

        fullSortCode = dd.sortCodePart1 + dd.sortCodePart2 + dd.sortCodePart3;

        if (bankAccountValidationResponseData && bankAccountValidationResponseData.success && bankAccountValidationResponseData.data) {
            accountInformationIsValid = bankAccountValidationResponseData.data.isCorrect;
            accountSupportsDirectDebit = bankAccountValidationResponseData.data.isDirectDebitCapable;

            bankAccountValidationResponseDataJson = bankAccountValidationResponseData.data;
        }

        onSetupDirectDebit(
            holdsUKBankAccountAndIsOwner,
            solePersonRequiredToAuthoriseDirectDebit,
            dd.accountName,
            fullSortCode,
            dd.sortCodePart1,
            dd.sortCodePart2,
            dd.sortCodePart3,
            dd.accountNumber,
            accountInformationIsValid,
            accountSupportsDirectDebit,
            bankAccountValidationResponseDataJson
        );

    };

    const cancelAction = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        onCancel();
    };

    const resetAction = () => {
        setBankAccountDetails(null);
        setDirectDebitInfo({
            accountName: "",
            sortCode: "",
            sortCodePart1: "",
            sortCodePart2: "",
            sortCodePart3: "",
            accountNumber: "",
            bankName: ""
        });


        setContinueButtonClicked(false);
        setBankAccountValidationResponse(null);

        onReset();
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        if (
            e.target.name == "sortCodePart1" ||
            e.target.name == "sortCodePart2" ||
            e.target.name == "sortCodePart3"
        ) {
            const numMatch = e.target.value.match(/^\d{0,2}$/);

            if (!numMatch) {
                return;
            }

            if (
                e.target.name == "sortCodePart1" &&
                e.target.value.length == 2 &&
                sortCodePart2Ref.current != null
            ) {
                sortCodePart2Ref.current.value = "";
                if (dd.sortCodePart2 != "" && dd.sortCodePart2 != null) {
                    setDirectDebitInfo({...dd, sortCodePart2: "", sortCodePart1: e.target.value});
                    sortCodePart2Ref.current.focus();
                    return;
                }
                sortCodePart2Ref.current.focus();
            }
            if (
                e.target.name == "sortCodePart2" &&
                e.target.value.length == 2 &&
                sortCodePart3Ref.current != null
            ) {
                sortCodePart3Ref.current.value = "";
                if (dd.sortCodePart3 != "" && dd.sortCodePart3 != null) {
                    setDirectDebitInfo({...dd, sortCodePart3: "", sortCodePart2: e.target.value});
                    sortCodePart3Ref.current.focus();
                    return;
                }
                sortCodePart3Ref.current.focus();
            }
            if (
                e.target.name == "sortCodePart3" &&
                e.target.value.length == 2 &&
                accountNumberRef.current != null
            ) {
                accountNumberRef.current.value = "";
                if (dd.accountNumber != "" && dd.accountNumber != null) {
                    setDirectDebitInfo({...dd, accountNumber: "", sortCodePart3: e.target.value});
                    accountNumberRef.current.focus();
                    return;
                }
                accountNumberRef.current.focus();
            }


        }

        if (e.target.name == "accountNumber") {
            const numMatch = e.target.value.match(/^\d{0,10}$/);

            if (!numMatch) {
                return;
            }
        }

        if (e.target.name == "accountName") {
            const fullAccountName = e.target.value;
            if (fullAccountName.length > 1) {
                if (fullAccountName.indexOf(" ") > -1) {
                    const nameParts = fullAccountName.split(" ");
                    for (let i = 0; i < nameParts.length; i++) {
                        if (nameParts[i].trim().length > 1) {
                            nameParts[i] = nameParts[i][0].toUpperCase() + nameParts[i].substr(1).toLowerCase();
                        } else {
                            nameParts[i] = nameParts[i].toUpperCase();
                        }
                    }
                    setDirectDebitInfo({...dd, accountName: nameParts.join(" ")});
                } else {
                    const fullAccountNameInitialCaps = fullAccountName[0].toUpperCase() + fullAccountName.substr(1).toLowerCase();
                    setDirectDebitInfo({...dd, accountName: fullAccountNameInitialCaps});
                }
            } else {
                setDirectDebitInfo({...dd, [e.target.name]: e.target.value});
            }
        } else {
            setDirectDebitInfo({...dd, [e.target.name]: e.target.value});
        }

        setContinueButtonClicked(false);


    };

    const handleKeyPress = (e) => {
        if (!alphaInputPattern.test(e.key)) {
            e.preventDefault();
        }
    };


    const handleNumericKeyPress = (e) => {
        if (!numericInputPattern.test(e.key)) {
            e.preventDefault();
        }
    };

    return (
        <>
            <DirectDebitContainer>
                <section id="direct-debit-details-container">
                    {!hiddenDataEntryFields &&
                        <div className="container">
                            <div className="row">
                                <div className="container-title">
                                    {containerTitleText ? containerTitleText : "Your Direct Debit instruction"}
                                </div>
                            </div>
                            <div className="row">
                                <label className="ui label direct-debit-details-label">Do you hold a UK bank account and
                                    you are
                                    the account holder?</label>
                                <Grid className="buttons-yes-no">
                                    <Grid.Row columns={2}>
                                        <Grid.Column>
                                            <Button fluid={true}
                                                    className={holdsUKBankAccountAndIsOwner === 1 ? "active" : ""}
                                                    onClick={() => {
                                                        setHoldsUKBankAccountAndIsOwnerQuestion(1);
                                                    }}>
                                                Yes
                                            </Button>
                                        </Grid.Column>
                                        <Grid.Column>
                                            <Button fluid={true}
                                                    className={holdsUKBankAccountAndIsOwner === 0 ? "active" : ""}
                                                    onClick={() => {
                                                        setHoldsUKBankAccountAndIsOwnerQuestion(0);
                                                    }}>
                                                No
                                            </Button>

                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                            </div>
                            <Fade>
                                <div className="row"
                                     style={holdsUKBankAccountAndIsOwner !== 1 ? {display: "none"} : {}}>
                                    <label className="ui label direct-debit-details-label">Are you the only person
                                        required to
                                        authorise debits from this account?</label>
                                    <Grid className="buttons-yes-no">
                                        <Grid.Row columns={2}>
                                            <Grid.Column>

                                                <Button fluid={true}
                                                        className={solePersonRequiredToAuthoriseDirectDebit === 1 ? "active" : ""}
                                                        onClick={() => {
                                                            setSolePersonRequiredToAuthoriseDirectDebitQuestion(1);
                                                        }}>
                                                    Yes
                                                </Button>
                                            </Grid.Column>
                                            <Grid.Column>
                                                <Button fluid={true}
                                                        className={solePersonRequiredToAuthoriseDirectDebit === 0 ? "active" : ""}
                                                        onClick={() => {
                                                            setSolePersonRequiredToAuthoriseDirectDebitQuestion(0);
                                                        }}>
                                                    No
                                                </Button>

                                            </Grid.Column>
                                        </Grid.Row>
                                    </Grid>
                                </div>
                            </Fade>
                            <Fade>
                                <div className="row"
                                     style={(!validAnswersToQuestions) ? {display: "none"} : {}}>
                                    <label className="ui label direct-debit-details-label">Account name</label>
                                    <Grid className="accountName-field">
                                        <Grid.Row>
                                            <Grid.Column>
                                                {!loading && bankAccountDetails !== null && <div className="readonly-account-field">{dd.accountName}</div>}
                                                {!loading && bankAccountDetails == null && <input
                                                    name="accountName"
                                                    required
                                                    maxLength={32}
                                                    ref={accountNameRef}
                                                    onChange={handleOnChange}
                                                    onKeyPress={handleKeyPress}
                                                    value={dd.accountName}
                                                    placeholder="Name of the account holder"
                                                />}
                                            </Grid.Column>
                                        </Grid.Row>
                                    </Grid>
                                </div>
                                <div className="row"
                                     style={(!validAnswersToQuestions) ? {display: "none"} : {}}>
                                    <label className="ui label direct-debit-details-label">Sort code</label>
                                    <Grid className="sortCode-field">
                                        <Grid.Row>
                                            {!loading && bankAccountDetails !== null && <Grid.Column><div className="readonly-account-field">{dd.sortCodePart1}-{dd.sortCodePart2}-{dd.sortCodePart3}</div></Grid.Column>}
                                            {!loading && bankAccountDetails == null &&
                                                <Grid.Column>
                                                    <input
                                                        name="sortCodePart1"
                                                        ref={sortCodePart1Ref}
                                                        type="tel"
                                                        required
                                                        inputMode='numeric'
                                                        placeholder="XX"
                                                        pattern="^[0-9]{2}$"
                                                        maxLength={2}
                                                        onChange={handleOnChange}
                                                        onKeyPress={handleNumericKeyPress}
                                                        value={dd.sortCodePart1}
                                                        className="short no-wrap sortCodePart1"
                                                    />
                                                    <span>-</span>
                                                    <input
                                                        name="sortCodePart2"
                                                        ref={sortCodePart2Ref}
                                                        type="tel"
                                                        required
                                                        inputMode='numeric'
                                                        placeholder="XX"
                                                        pattern="^[0-9]{2}$"
                                                        maxLength={2}
                                                        onChange={handleOnChange}
                                                        onKeyPress={handleNumericKeyPress}
                                                        value={dd.sortCodePart2}
                                                        className="short no-wrap sortCodePart2"
                                                    />
                                                    <span>-</span>
                                                    <input
                                                        name="sortCodePart3"
                                                        type="tel"
                                                        ref={sortCodePart3Ref}
                                                        required
                                                        inputMode='numeric'
                                                        placeholder="XX"
                                                        pattern="^[0-9]{2}$"
                                                        maxLength={2}
                                                        onChange={handleOnChange}
                                                        onKeyPress={handleNumericKeyPress}
                                                        value={dd.sortCodePart3}
                                                        className="short no-wrap sortCodePart3"
                                                    />
                                                </Grid.Column>
                                            }
                                        </Grid.Row>
                                    </Grid>
                                </div>
                                <div className="row"
                                     style={(!validAnswersToQuestions) ? {display: "none"} : {}}>
                                    <label className="ui label direct-debit-details-label">Account number</label>
                                    <Grid className="accountNumber-field">
                                        <Grid.Row>
                                            <Grid.Column>
                                                {!loading && bankAccountDetails !== null && <div className="readonly-account-field">{dd.accountNumber}</div>}
                                                {!loading && bankAccountDetails == null &&
                                                    <input
                                                        name="accountNumber"
                                                        type="tel"
                                                        ref={accountNumberRef}
                                                        required
                                                        inputMode='numeric'
                                                        placeholder="Account number"
                                                        maxLength={8}
                                                        onChange={handleOnChange}
                                                        onKeyPress={handleNumericKeyPress}
                                                        value={dd.accountNumber}
                                                    />
                                                }
                                            </Grid.Column>
                                        </Grid.Row>
                                    </Grid>
                                </div>
                                {!loading && bankAccountDetails !== null &&
                                    <div className="resetDirectDebitDetailsContainer">
                                        <Label className="resetDirectDebitDetails" as="a"
                                               onClick={() => { resetAction(); }}>Reset</Label>
                                    </div>
                                }

                                {!loading && bankAccountDetails !== null &&
                                    <div className="row">
                                        <label className="ui label direct-debit-details-label">Branch</label>
                                        <Grid className="bankAccountDetails-field">
                                            <Grid.Row>
                                                <Grid.Column>
                                                    <div
                                                        className="direct-debit-bank-details">{formatBankDetails(bankAccountDetails)}</div>
                                                </Grid.Column>
                                            </Grid.Row>
                                        </Grid>
                                    </div>}
                                {!loading && bankAccountDetails !== null &&
                                    <div className="row">
                                        <Grid className="bankAccountDetails2-field">
                                            <Grid.Row>
                                                <Grid.Column>
                                                    <div className="direct-debit-bank-details-small">Your Direct Debit
                                                        Instruction and
                                                        Guarantee will be confirmed to you by email within 3 working
                                                        days or no later than
                                                        10 working days prior to the first collection – as long as that
                                                        takes place within the first
                                                        month. The company name which will appear on your
                                                        bank statement against the Direct Debits will be &apos;Alan
                                                        Boswell Insurance&apos; and our
                                                        service user number is 433912.
                                                    </div>
                                                </Grid.Column>
                                            </Grid.Row>
                                        </Grid>
                                    </div>}
                                {!loading && bankAccountDetails !== null &&
                                    <div className="row">
                                        <Grid className="bankAccountDetails2-field">

                                            <Grid.Row>
                                                <Grid.Column>
                                                    <div className="direct-debit-bank-details-small">If you need to
                                                        contact us about this
                                                        Direct Debit our details are: Alan Boswell Insurance Brokers
                                                        Ltd, Prospect House,
                                                        Rouen Road, Norwich NR1 1RE. Telephone: <a
                                                            href="tel:01603216395">01603 216395</a> or
                                                        email: <a
                                                            href="mailto:instalments@alanboswell.com">instalments@alanboswell.com</a>
                                                    </div>
                                                </Grid.Column>
                                            </Grid.Row>
                                        </Grid>
                                    </div>
                                }
                            </Fade>
                        </div>
                    }
                    {errorMessage &&
                        <div className="errorMessage-field">
                            <span className="direct-debit-error">{errorMessage}</span>
                        </div>
                    }

                    {loading &&
                        <Grid className="loading-field">
                            <Grid.Row>
                                <Grid.Column>
                                    <button className={`purchase stage-10`} type="button" disabled>
                                        <span id="lock"></span>
                                        <Icon name={"spinner"} loading/>
                                    </button>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    }

                    {!loading && !canSetupDirectDebit && !hiddenDataEntryFields && displayContinueButton && !continueButtonClicked && (
                        <div className="buttonContainer continue">

                            <button
                                className={`purchase stage-10`}
                                type="button"
                                disabled={!isV}
                                onClick={_.debounce(continueAction, 1000, {
                                    'leading': true,
                                    'trailing': false
                                })}
                            >
                                Continue
                            </button>

                        </div>
                    )}
                    {!loading && !hiddenDataEntryFields && continueButtonClicked && canSetupDirectDebit && (
                        <div className="buttonContainer setupDirectDebit">

                            <button
                                className={`purchase stage-10`}
                                type="button"
                                disabled={!isV}
                                onClick={_.debounce(setupDirectDebitAction, 1000, {
                                    'leading': true,
                                    'trailing': false
                                })}
                            >
                                Set Up Direct Debit
                            </button>

                        </div>)}
                    {!loading && displayCancelButton && (
                        <div className="buttonContainer cancel">

                            <button
                                className={`cancel stage-10`}
                                type="button"
                                onClick={_.debounce(cancelAction, 1000, {
                                    'leading': true,
                                    'trailing': false
                                })}
                            >
                                Cancel
                            </button>

                        </div>
                    )}

                    <div className="row">
                        <div id="card-logos">
                            <div className="card-logo-direct-debit"></div>
                        </div>
                    </div>

                </section>
            </DirectDebitContainer>
        </>
    );
};

const DirectDebitContainer = styled.div`
    #direct-debit-details-container {
        margin: 0 auto;
        font-family: "FigTree-Standard", sans-serif !important;
    }

    .container {
        border: 4px solid #dae2e8;
        padding-top: 12px;
        color: rgb(16, 63, 111);

        .container-title {
            text-align: left;
            font-size: 17px;
        }
    }

    .row {
        text-align: left;
        padding: 0px 0px 8px 0px;
        box-sizing: border-box;
        display: block;
        margin: 0 !important;

        .column-one {
            display: inline-block;
            width: 50%;
        }

        .column-two {
            display: inline-block;
            width: 50%;
        }
    }

    .direct-debit-details-label {
        font-family: "FigTree-Medium", sans-serif !important;
        background: none;
        -webkit-text-decoration: none;
        text-decoration: none;
        font-weight: normal;
        display: block;
        padding-left: 0;
        color: rgb(16,63,111);
        font-size: 15px;
        line-height:1.5em;
    }

    .direct-debit-bank-details
    {
        white-space: pre-wrap;
        border: 1px solid #e0e0e0 !important;
        padding: 10px !important;
        margin-bottom:8px !important;
        background:#f5f8f9;
        font-size: 12px;
        border-radius: 5px;
    }

    .direct-debit-bank-details-small
    {
        white-space: pre-wrap;
        margin-bottom:8px !important;
        font-size: 12px;
    }

    input {

        padding: 10px;
        margin: 0px;
        border: 1px solid rgba(34,36,38,0.15);
        width: 100%;
        border-radius: 5px;
        font-size: 0.85em;
        color: #103f6f;

        &[name="expyy"] {
            margin-right: 20px;
        }

        &.short {
            width: 65px !important;
            margin-right: 10px;

            @media (min-width: 350px) {
                width: 50px !important;
                margin-right: 5px;
            }
        }

        &[name="accountName"] {
            width:100%;
        }

        &[name="accountNumber"] {
            width:190px
        }

    }

    .sortCode-field input
    {
        text-align:center;
        margin-right:0px;
    }
    .sortCode-field span
    {
        margin: 0px 5px;
        padding: 0px 2px;
        text-align: center;
        font-size: 18px;
    }

    input[name="number"] {
        background-position: right 5px center;
        background-repeat: no-repeat;
        background-size: auto 30px;
    }

    button {
        background-color: #21ba45;
        color: #fff;
        width: 100%;
        padding: 12px 20px;
        font-size: 16px;
        cursor: pointer;
        display: inline-block;
        min-height: 1em;
        outline: 0;
        border: none;
        vertical-align: baseline;

        &[disabled] {
            opacity: 0.5;
            cursor: default;
        }
    }


    button.purchase
    {
        margin-bottom:8px;
    }

    button.cancel
    {
        background-color:#e0e0e0;
        color:#000;
    }

    #card-logos {
        text-align: center;
        margin-top:8px;

        > div {
            width: 120px;
            height: 52px;
            margin: 0 5px;
            display: inline-block;
            background-position: center;
            background-repeat: no-repeat;
            background-size: auto 46px;
            padding: 3px;
            background-color: #fff;
        }
    }

    .card-logo-direct-debit {
        background-image: url(${ddebit});
    }

    .direct-debit-error {
        color: red;
        font-weight: bold;
        display: inline-block;
        text-align: center;
        padding-bottom:4px;
    }

    .ui.buttons {
        width: auto;
    }
    .buttons-yes-no .button,
    .buttons-yes-no .button:first-child {
        margin-right: 8px;
        border: 1px solid rgb(16, 63, 111);
        background-color: white;
        color: rgb(16, 63, 111);
    }

    .buttons-yes-no .button:last-child {
        margin-right: 0;
    }

    .buttons-yes-no .button:hover,
    .buttons-yes-no .button.active {
        border: 1px solid rgb(16, 63, 111);
        background-color: rgb(16, 63, 111);
        color: white;
    }

    .ui.grid,
    .ui.grid > .row
    {
        margin: 0;
        padding: 0;
    }

    .buttons-yes-no .column:first-child {
        padding-right: 0.1rem !important;
    }
    .buttons-yes-no .column:last-child {
        padding-left: 0.1rem !important;
    }

    .buttons-yes-no .ui.buttons {
        width: 100%;
    }

    .errorMessage-field
    {
        margin-bottom:16px;
    }

    .buttons-yes-no .ui.active.button
    {
        background-color:rgb(16,63,111)
    }

    .buttonContainer
    {
        margin-bottom:0px;
    }

    .resetDirectDebitDetailsContainer
    {
    }

    .resetDirectDebitDetails
    {
        background: none !important;
        text-decoration: underline !important;
        padding: 0 8px 0 0 !important;
        color: #103f6f !important;
        font-size: 1em !important;
        margin-bottom:24px !important;
    }

    .readonly-account-field
    {
        font-size: 12px;
        border-radius: 5px;
        white-space: pre-wrap;
        background: rgb(245, 248, 249);
        border: 1px solid rgb(224, 224, 224) !important;
        padding: 10px !important;
        margin-bottom: 8px !important;
        color:rgb(16, 63, 111);
    }

`;


const fadeIn = keyframes`
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
`;

const fadeOut = keyframes`
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
`;

const Fade = styled.div`
    visibility: ${props => props.out ? 'hidden' : 'visible'};
    animation: ${props => props.out ? fadeOut : fadeIn} 1s linear;
    transition: visibility 1s linear;
`;
